import { useState } from 'react';
import { apiKeys, type ApiKeys } from '@/lib/store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Save, Eye, EyeOff } from 'lucide-react';

const fields: { key: keyof ApiKeys; label: string; placeholder: string }[] = [
  { key: 'googleAnalyticsId', label: 'Google Analytics 4 ID', placeholder: 'G-XXXXXXXXXX' },
  { key: 'googleAdsId', label: 'Google Ads ID', placeholder: 'AW-XXXXXXXXX' },
  { key: 'googleTagManagerId', label: 'Google Tag Manager ID', placeholder: 'GTM-XXXXXXX' },
  { key: 'bingApiKey', label: 'Bing API Key', placeholder: 'Enter Bing API key' },
  { key: 'googleMapsApiKey', label: 'Google Maps API Key', placeholder: 'Enter Maps API key' },
  { key: 'facebookAppId', label: 'Facebook App ID', placeholder: 'your-fb-app-id' },
];

export default function ApiConfig() {
  const [config, setConfig] = useState<ApiKeys>(apiKeys.get());
  const [showKeys, setShowKeys] = useState<Record<string, boolean>>({});

  const handleChange = (key: keyof ApiKeys, value: string) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    apiKeys.set(config);
    toast.success('API configuration saved successfully');
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-heading text-2xl font-bold text-white">API Configuration</h1>
        <p className="text-white/60 text-sm mt-1">Manage your Google, Bing, and third-party API keys.</p>
      </div>

      <div className="card-dark p-6 space-y-5">
        {fields.map(({ key, label, placeholder }) => (
          <div key={key} className="space-y-2">
            <Label className="text-white/80 text-sm">{label}</Label>
            <div className="relative">
              <Input
                type={showKeys[key] ? 'text' : 'password'}
                value={config[key]}
                onChange={(e) => handleChange(key, e.target.value)}
                placeholder={placeholder}
                className="h-11 bg-[#0057B7] border-white/10 text-white pr-10"
              />
              <button
                type="button"
                onClick={() => setShowKeys(prev => ({ ...prev, [key]: !prev[key] }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white/60"
              >
                {showKeys[key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        ))}

        <div className="pt-4 border-t border-white/10">
          <Button onClick={handleSave} className="btn-primary">
            <Save className="w-4 h-4" />
            Save API Keys
          </Button>
        </div>
      </div>

      {/* Status */}
      <div className="card-dark p-6">
        <h3 className="font-heading font-semibold text-white mb-4">Connection Status</h3>
        <div className="space-y-3">
          {fields.map(({ key, label }) => {
            const isSet = !!config[key];
            return (
              <div key={key} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                <span className="text-sm text-white/70">{label}</span>
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                  isSet ? 'bg-[#22c55e]/10 text-[#22c55e]' : 'bg-white/5 text-white/60'
                }`}>
                  {isSet ? 'Configured' : 'Not Set'}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}